<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { HomePageType } from "~/api";
import { authStore } from "~/stores/authStore";
import { AuthStoreType } from "~/types/auth";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { refreshCaches } from "~/utils/miscUtils";

const myInfoStore = useMyInfoStore();
const router = useRouter();

const isLoggingIn = ref(false);

const userLogin = async () => {
  if (isLoggingIn.value) {
    console.log("Login already in progress, ignoring duplicate request");
    return;
  }

  isLoggingIn.value = true;

  try {
    await authStore.login(AuthStoreType.User);
  } catch (err) {
    console.warn("Login failed:", err);
  } finally {
    // Reset after a delay to prevent rapid clicking
    setTimeout(() => {
      isLoggingIn.value = false;
    }, 2000);
  }
};

const isLoggedIn = computed(() => !!myInfoStore.userInfo.loginName);
const hasAnOrg = computed(() => myInfoStore.mySysOrgs.length !== 0 || false);

const loading = ref(true);

const notFound = computed(() => {
  const path =
    // router.currentRoute.value.redirectedFrom?.path ||
    router.currentRoute.value.path;
  if (!path) {
    return "";
  }
  return path !== "/landing" ? path.substring(1) : "";
});

const reload = async () => {
  await refreshCaches();
  router.push("/");
};

const homePageType = ref<HomePageType>(HomePageType.DEFAULT);

onMounted(async () => {
  await myInfoStore.load();

  homePageType.value =
    myInfoStore.currentSysOrg?.entityDto?.homePage ?? HomePageType.DEFAULT;

  loading.value = false;
});

// const testSignalR1 = async () => {
//   await siteWideHub?.invoke(
//     "SendMessageToServer",
//     "Test message from Vue via hub",
//   );
// };
// <el-button type="info" @click="testSignalR1">Test SignalR</el-button>
</script>

<template>
  <div class="landingPage">
    <p class="main">Welcome to Manifast Business Tools</p>
    <div v-if="notFound" class="notFound">
      <p>Page not found:</p>
      <p>
        <strong>{{ notFound }}</strong>
      </p>
      <el-button type="info" @click="router.push({ path: '/' })"
        >View the Home Page</el-button
      >
      <p v-if="myInfoStore.userInfo">
        <el-button type="info" @click="reload">Refresh My Settings</el-button>
      </p>
    </div>
    <div v-if="loading" class="loading">
      <p>Checking your account...</p>
    </div>
    <div v-else>
      <p v-if="!isLoggedIn">
        Click <el-link @click="userLogin">Sign in</el-link> to get started
      </p>
      <div v-else-if="!hasAnOrg" class="noOrgs">
        <p>Welcome {{ myInfoStore.userInfo?.loginName }}.</p>
        <p>You are logged in, but are not associated with any organization.</p>
        <p>
          Please contact your administrator and ask them to add you with your
          email address ({{ myInfoStore.userInfo?.email }}).
        </p>
      </div>
      <div v-else-if="homePageType === HomePageType.WORKFLOWS">
        <p>
          Welcome back, {{ myInfoStore.userInfo?.loginName }}. You are logged in
          to the organization "{{ myInfoStore.currentSysOrg?.name }}".
        </p>
        <el-button type="info" @click="router.push({ path: '/' })"
          >View your Home Page</el-button
        >
      </div>
    </div>
    <el-image src="/images/compliance.png" @click="userLogin" />
    <!-- <el-skeleton :rows="5" animated />
    <p></p>
    <el-skeleton :rows="5" animated /> -->
  </div>
</template>

<style lang="less">
.landingPage {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1em;

  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  align-items: center;

  .loading {
    font-size: 1.1em;
    height: 5em;
    display: flex;
    flex-direction: column;
  }
  .main {
    font-size: 1.3em;
  }
  .dump {
    white-space: pre;
    user-select: text;
    max-height: 30vh;
    overflow: auto;
    display: none; // Set to block to show the dump
  }
  .noOrgs {
    padding: 0.5em 1.5em;
    border: 1px solid var(--el-color-primary);
    box-shadow: 0 0 10px var(--el-color-primary);
  }
  .notFound {
    padding: 0 2em 0.5em;
    text-align: center;
    border: 1px solid var(--el-color-danger);
    box-shadow: 0 0 10px var(--el-color-danger);
  }
  .el-link {
    vertical-align: baseline;
    padding: 0 2px;
    font-size: inherit;
  }
  .el-image {
    position: relative;
    margin: 0 0 50px;
    width: 800px;
    height: 650px;
    border-radius: 50%; /* Creates the oval shape */
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        ellipse,
        rgba(0, 0, 255, 0) 60%,
        rgba(0, 0, 70, 1) 100%
      );
      pointer-events: none; /* Allows interaction with the image below */
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover; /* Ensures the image fills the frame */
    }
  }
}
</style>
